import React, { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Upload, Loader2, Trash2, FileText, Check, X, Building2, ShieldCheck } from 'lucide-react';

const capit = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

export default function GaranzieComparatore() {
  const [dips, setDips] = useState([]);
  const [prodotti, setProdotti] = useState([]);
  const [prodotto, setProdotto] = useState('');
  const [compare, setCompare] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [d, p] = await Promise.all([api.get('/garanzie'), api.get('/garanzie/prodotti')]);
      setDips(d.data || []);
      setProdotti(p.data || []);
      if ((p.data || []).length && !prodotto) setProdotto(p.data[0]);
    } catch { toast.error('Errore caricamento'); } finally { setLoading(false); }
  }, [prodotto]);

  useEffect(() => { loadAll(); }, []); // eslint-disable-line

  const loadCompare = useCallback(async (prod) => {
    if (!prod) { setCompare(null); return; }
    try { const { data } = await api.get('/garanzie/compare', { params: { prodotto: prod } }); setCompare(data); }
    catch { setCompare(null); }
  }, []);

  useEffect(() => { loadCompare(prodotto); }, [prodotto, loadCompare, dips]);

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    const fd = new FormData(); fd.append('file', f);
    try {
      const { data } = await api.post('/garanzie/upload-dip', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`DIP caricato: ${data.compagnia} — ${data.prodotto} (${data.garanzie?.length || 0} garanzie)`);
      await loadAll();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Caricamento DIP fallito'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const del = async (id) => {
    if (!window.confirm('Eliminare questo DIP?')) return;
    try { await api.delete(`/garanzie/${id}`); toast.success('Eliminato'); await loadAll(); }
    catch { toast.error('Errore'); }
  };

  const cell = (val) => {
    if (!val) return <X size={14} className="text-slate-300 mx-auto" />;
    return (
      <div className="text-center leading-tight">
        {val.incluso
          ? <Check size={14} className="text-green-600 mx-auto" />
          : <span className="text-[10px] text-amber-600 font-medium">opzionale</span>}
        {val.massimale && <div className="text-[10px] text-slate-600 mt-0.5">Max: {val.massimale}</div>}
        {val.franchigia && <div className="text-[10px] text-orange-600">Fr: {val.franchigia}</div>}
        {val.scoperto && <div className="text-[10px] text-rose-600">Sc: {val.scoperto}</div>}
        {val.limiti && <div className="text-[9px] text-slate-400">{val.limiti}</div>}
      </div>
    );
  };

  return (
    <div className="p-4 sm:p-6 max-w-[1400px] mx-auto space-y-5" data-testid="garanzie-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Garanzie / Comparatore</h1>
          <p className="text-sm text-slate-500">Carica i DIP delle polizze: l'AI estrae le garanzie e le confronta tra compagnie.</p>
        </div>
        <label>
          <input type="file" accept=".pdf" className="hidden" onChange={onUpload} data-testid="dip-upload-input" />
          <Button asChild disabled={uploading}>
            <span className="cursor-pointer">{uploading ? <Loader2 size={15} className="animate-spin mr-1" /> : <Upload size={15} className="mr-1" />} Carica DIP (PDF)</span>
          </Button>
        </label>
      </div>
      {uploading && <p className="text-xs text-indigo-600">Estrazione AI in corso… può richiedere qualche secondo.</p>}

      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <h2 className="font-semibold text-slate-800 mb-3 flex items-center gap-2"><FileText size={16} /> DIP caricati ({dips.length})</h2>
        {loading ? (
          <div className="flex justify-center py-6 text-slate-400"><Loader2 size={22} className="animate-spin" /></div>
        ) : dips.length === 0 ? (
          <p className="text-sm text-slate-400">Nessun DIP caricato. Carica un PDF per iniziare.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {dips.map((d) => (
              <div key={d.id} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5 text-sm" data-testid={`dip-${d.id}`}>
                <Building2 size={14} className="text-slate-400" />
                <span className="font-medium">{d.compagnia}</span>
                <Badge className="bg-indigo-100 text-indigo-700 border-0 text-[10px]">{capit(d.prodotto_norm || d.prodotto)}</Badge>
                <span className="text-xs text-slate-400">{d.garanzie?.length || 0} gar.</span>
                <button onClick={() => del(d.id)} className="text-slate-400 hover:text-red-500" data-testid={`del-dip-${d.id}`}><Trash2 size={13} /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-center gap-3 mb-3">
          <h2 className="font-semibold text-slate-800 flex items-center gap-2"><ShieldCheck size={16} /> Comparatore garanzie</h2>
          {prodotti.length > 0 && (
            <Select value={prodotto} onValueChange={setProdotto}>
              <SelectTrigger className="h-9 w-56" data-testid="prodotto-compare-select"><SelectValue placeholder="Scegli un prodotto" /></SelectTrigger>
              <SelectContent>
                {prodotti.map((p) => <SelectItem key={p} value={p}>{capit(p)}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
        {!compare || compare.compagnie.length === 0 ? (
          <p className="text-sm text-slate-400">Carica almeno 2 DIP dello stesso prodotto per confrontarli.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse" data-testid="compare-table">
              <thead>
                <tr>
                  <th className="text-left p-2 bg-slate-100 sticky left-0">Garanzia</th>
                  {compare.compagnie.map((c) => <th key={c} className="p-2 bg-slate-100 text-center min-w-[120px]">{c}</th>)}
                </tr>
              </thead>
              <tbody>
                {compare.garanzie.map((g, i) => (
                  <tr key={i} className={g.differenza ? 'bg-amber-50' : ''} data-testid={`compare-row-${i}`}>
                    <td className="p-2 border-t border-slate-100 font-medium sticky left-0 bg-inherit">
                      {g.garanzia}
                      {g.differenza && <Badge className="bg-amber-200 text-amber-800 border-0 text-[9px] ml-1">differenza</Badge>}
                    </td>
                    {compare.compagnie.map((c) => <td key={c} className="p-2 border-t border-slate-100 text-center align-middle">{cell(g.colonne[c])}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-[11px] text-slate-400 mt-2">Le righe in giallo evidenziano le differenze tra compagnie (presenza, massimale, franchigia o scoperto diversi). ✓ = inclusa · "opzionale" = facoltativa · Max = massimale · Fr = franchigia · Sc = scoperto · vuoto = non prevista.</p>
          </div>
        )}
      </div>
    </div>
  );
}
